import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger, PopoverArrow } from "./ui/popover";
import { Separator } from "./ui/separator";
import { supabase } from "@/integrations/supabase/client";

export const LoginPopover = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleLogin = async () => {
    setIsLoading(true);
    setError(null);
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    setIsLoading(false);
    
    if (error) {
      setError(error.message);
    } else {
      setIsOpen(false);
      setEmail("");
      setPassword("");
    }
  };

  const handleSignUp = async () => {
    setIsLoading(true);
    setError(null);
    
    const redirectUrl = `${window.location.origin}/recipes`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    
    setIsLoading(false);
    
    if (error) {
      setError(error.message);
    } else {
      setError("Check your email to confirm your account!");
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/recipes`,
      },
    });
    
    if (error) {
      setError(error.message);
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && email && password && !isLoading) {
      handleLogin();
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          className="bg-[hsl(160,12%,35%)] hover:bg-[hsl(160,12%,28%)] text-white rounded-full px-5"
        >
          Login
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 rounded-2xl border-border/50 bg-card shadow-xl shadow-primary/5 p-6" 
        align="end"
        sideOffset={8}
      >
        <PopoverArrow className="fill-card" />
        
        {/* Decorative header */}
        <div className="text-center mb-5">
          <h3 className="text-lg font-medium text-foreground">Welcome Back</h3>
          <p className="text-sm text-muted-foreground mt-1">Sign in to your account</p>
        </div>
        
        <div className="space-y-4">
          <Input
            id="email"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            className="rounded-xl border-border/60 bg-background/50 focus:border-primary/50 focus:ring-primary/20 h-11"
          />
          <Input
            id="password"
            type="password"
            placeholder="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            className="rounded-xl border-border/60 bg-background/50 focus:border-primary/50 focus:ring-primary/20 h-11"
          />

          <div className="relative py-2">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
              or
            </span>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full h-11 gap-2 rounded-xl border-border/60 hover:bg-secondary/50"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>
          
          {error && (
            <p className={`text-sm text-center ${error.includes("Check your email") ? "text-green-600" : "text-destructive"}`}>
              {error}
            </p>
          )}
          
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1 rounded-xl border-border/60 hover:bg-accent/50 h-11"
              onClick={handleSignUp}
              disabled={isLoading || !email || !password}
            >
              Sign Up
            </Button>
            <Button
              className="flex-1 rounded-xl bg-primary hover:bg-primary/90 h-11 shadow-sm"
              onClick={handleLogin}
              disabled={isLoading || !email || !password}
            >
              Login
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
